import React, { MouseEvent, useCallback, useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import WithMenu from '@hoc/WithMenu/WithMenu'
import { Pages } from '@models/Pages'
import { APP_SLUG } from '@models/AppType'
import { SurveyContext } from '@context/SurveyContext'
import { ConsentContext } from '@context/ConsentContext'
import { useCategories } from '@hooks/useCategories'
import environment from 'config/environment'
import { isSurveyInProgress, switchEdition } from '@utils/switchEdition'
import EditionSwitchConfirmModal from './EditionSwitchConfirmModal'

import closeIcon from '@assets/images/nav/close.png'
import closeHoverIcon from '@assets/images/nav/close-hover.png'
import gotchi from '@assets/images/characters/gotchi-shadow.png'
import externalLinkIcon from '@assets/images/nav/share-active.png'
import chevronDownIcon from '@assets/images/icons/chevron-down-idle.png'
import chevronUpIcon from '@assets/images/icons/chevron-up-idle.png'
import { handleImageMouseEnterOrLeave } from '@components/ModalDialog/ModalDialog'

import './EditionsPage.scss'

export function EditionsPage() {
  const { t } = useTranslation('Editions')
  const { t: tMenu } = useTranslation('MainMenu')
  const navigate = useNavigate()
  const { step } = useContext(SurveyContext)
  const { hasConsented } = useContext(ConsentContext)
  const { categories, archives, isLoading, error } = useCategories()
  const [pendingSlug, setPendingSlug] = useState<string | undefined>()
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [showArchives, setShowArchives] = useState(false)

  const handleHeaderClose = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()
      navigate(`/${Pages.Home}`)
    },
    [navigate],
  )

  const performSwitch = useCallback((accessSlug: string) => {
    void switchEdition(accessSlug)
  }, [])

  const handleEditionClick = useCallback(
    (accessSlug: string) => {
      if (accessSlug === APP_SLUG) {
        navigate(`/${Pages.Home}`)
        return
      }

      if (isSurveyInProgress(step, hasConsented)) {
        setPendingSlug(accessSlug)
        setConfirmVisible(true)
        return
      }

      performSwitch(accessSlug)
    },
    [step, hasConsented, navigate, performSwitch],
  )

  const handleConfirm = useCallback(() => {
    if (pendingSlug) {
      performSwitch(pendingSlug)
    }
    setConfirmVisible(false)
    setPendingSlug(undefined)
  }, [pendingSlug, performSwitch])

  const handleCancel = useCallback(() => {
    setConfirmVisible(false)
    setPendingSlug(undefined)
  }, [])

  const handleArchivesToggle = useCallback(() => {
    setShowArchives((value) => !value)
  }, [])

  useEffect(() => {
    if (archives.some((category) => category.accessSlug === APP_SLUG)) {
      setShowArchives(true)
    }
  }, [archives])

  const showArchivesSection = environment.isDev && archives.length > 0

  return (
    <div className="EditionsPage">
      <div className="MainMenu EditionsPage-menu">
        <Link to={`/${Pages.Home}`} className="MainMenu-header-link">
          <header className="MainMenu-header">
            <img src={gotchi} alt={t('pageTitle')} />

            <button type="button" onClick={handleHeaderClose}>
              <img
                src={closeIcon}
                alt={tMenu('close')}
                onMouseOver={(e) => handleImageMouseEnterOrLeave(e, closeHoverIcon)}
                onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, closeIcon)}
              />
            </button>
          </header>
          <div className="MainMenu-title">
            <h1>
              {t('pageTitle')}
              <small>{t('subTitle')}</small>
            </h1>
          </div>
        </Link>

        <div className="MainMenu-body EditionsPage-list">
          <a
            className="MainMenu-body-link EditionsPage-external"
            href="https://datagotchi.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t('globalSite')} (${t('openInNewTab')})`}>
            <h3 className="EditionsPage-external-label">
              <span>{t('globalSite')}</span>
              <img
                className="EditionsPage-external-icon"
                src={externalLinkIcon}
                alt=""
                aria-hidden="true"
              />
            </h3>
          </a>
          {isLoading && <p className="EditionsPage-status">{t('loading')}</p>}
          {error && !isLoading && <p className="EditionsPage-status EditionsPage-status--error">{t('error')}</p>}
          {!isLoading && !error && categories.length === 0 && (
            <p className="EditionsPage-status">{t('empty')}</p>
          )}
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              className={clsx('MainMenu-body-link', {
                active: category.accessSlug === APP_SLUG,
              })}
              onClick={() => handleEditionClick(category.accessSlug)}>
              <h3>{category.name}</h3>
            </button>
          ))}
          {showArchivesSection && (
            <>
              <button
                type="button"
                className={clsx('MainMenu-body-link', {
                  active: showArchives,
                })}
                onClick={handleArchivesToggle}
                aria-expanded={showArchives}>
                <h3>{t('archives')}</h3>
                <img className="suffix" src={showArchives ? chevronUpIcon : chevronDownIcon} alt="" />
              </button>
              {showArchives &&
                archives.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={clsx('Submenu-item', {
                      selected: category.accessSlug === APP_SLUG,
                    })}
                    onClick={() => handleEditionClick(category.accessSlug)}>
                    {category.name}
                  </button>
                ))}
            </>
          )}
        </div>
      </div>

      <EditionSwitchConfirmModal visible={confirmVisible} onCancel={handleCancel} onConfirm={handleConfirm} />
    </div>
  )
}

export default WithMenu(EditionsPage)
